/** Phase + stage colour/glyph model for the deals map mark. */
export interface DealPhase {
  id: string;
  label: string;
}

export const DEAL_PHASES: readonly DealPhase[] = [
  { id: 'lead', label: 'Prospection' },
  { id: 'rec', label: 'Committee' },
  { id: 'offer', label: 'Offer & negotiation' },
  { id: 'secured', label: 'Securing' },
  { id: 'won', label: 'Won' },
  { id: 'closed', label: 'Closed' },
];

/** The one red both closed stages share, and which appears nowhere among the live phases. */
export const DEAL_CLOSED_COLOR = '#A61F0D';

export interface DealStageStyle {
  stage: string;
  phase: string;
  /** The mark's fill. Every stage is filled the same way — a dot behind a white ring. */
  color: string;
  /** Glyph colour — the legible variant of `color`, which a pale or vivid fill needs. */
  glyph: string;
  /**
   * The arc and its discrete twin on the card. Separate from `color` because a pale fill cannot
   * read against the ring's grey track.
   */
  arc: string;
  /** What anything filled with `color` writes in. */
  fg: string;
  /** Pictogram to draw — defaults to `phase`. */
  glyphId: string;
  /** No arc — a closed deal has no place in the pipeline. */
  hollow: boolean;
}

function stageStyle(
  stage: string,
  phase: string,
  color: string,
  opts: {
    glyph?: string;
    glyphId?: string;
    hollow?: boolean;
    arc?: string;
    fg?: string;
  } = {},
): DealStageStyle {
  const arc = opts.arc ?? color;
  return {
    stage,
    phase,
    color,
    glyph: opts.glyph ?? arc,
    arc,
    fg: opts.fg ?? '#FFFFFF',
    glyphId: opts.glyphId ?? phase,
    hollow: opts.hollow ?? false,
  };
}

/**
 * The nine official HubSpot pipeline stages, in the pipeline's own order — which is what drives the
 * ring's arc, so moving a row here moves every arc after it.
 *
 * One fill per phase — zone H, zone R, zone UH, the Electra navy, vivid mint.
 */
export const DEAL_STAGE_STYLES: readonly DealStageStyle[] = [
  stageStyle('Identified Lead', 'lead', '#096CDC'),
  stageStyle('Activated Lead', 'lead', '#096CDC'),
  stageStyle('REC preparation', 'rec', '#00A36D', { glyphId: 'review' }),
  stageStyle('Offer in preparation', 'offer', '#7C3AED'),
  stageStyle('Offer sent', 'offer', '#7C3AED'),
  stageStyle('Negotiations', 'offer', '#7C3AED'),
  stageStyle('TS Signed', 'secured', '#0B2936'),
  stageStyle('Lease Signed', 'won', '#43F5BA', { fg: '#021715' }),
  // Hollow, so live vs dead is a difference in construction and not in hue alone. `fg` is the red
  // rather than white: a hollow mark writes in its own colour on a white body.
  stageStyle('Lost', 'closed', DEAL_CLOSED_COLOR, {
    glyphId: 'lost',
    hollow: true,
    fg: DEAL_CLOSED_COLOR,
  }),
];

/** The known stage labels, derived so the two lists can never drift. */
export const DEAL_STAGES: readonly string[] = DEAL_STAGE_STYLES.map((s) => s.stage);

/** Stages with a place in the pipeline; a closed stage has none and carries no arc. */
export const DEAL_LIVE_STAGE_STYLES: readonly DealStageStyle[] = DEAL_STAGE_STYLES.filter(
  (s) => !s.hollow,
);

const STYLE_BY_STAGE = new Map(DEAL_STAGE_STYLES.map((s) => [s.stage, s]));

/** Neutral fallback for a HubSpot stage outside `DEAL_STAGE_STYLES` (renamed/added upstream). */
export const UNKNOWN_STAGE_STYLE: DealStageStyle = stageStyle('unknown', 'closed', '#8A9391', {
  glyphId: 'canceled',
  hollow: true,
});

export function dealStageStyle(stage: string): DealStageStyle {
  return STYLE_BY_STAGE.get(stage) ?? UNKNOWN_STAGE_STYLE;
}

/** The stage's 1-based place in the live pipeline; 0 for a closed/unknown stage. */
export function dealStagePosition(stage: string): number {
  return DEAL_LIVE_STAGE_STYLES.findIndex((s) => s.stage === stage) + 1;
}

/** Percentage of the live pipeline completed by this stage (0 for closed/unknown). */
export function dealStageProgress(stage: string): number {
  const position = dealStagePosition(stage);
  return position === 0 ? 0 : Math.round((position / DEAL_LIVE_STAGE_STYLES.length) * 100);
}
